/**
 * Service layer for the Delivery Runs console. Thin typed wrappers over
 * `integrationsClient` + `endpoints.deliveries`. Responses are camelCase and
 * pass through as-is. Request bodies mostly do too — the one exception is the
 * trigger body, whose backend schema only accepts snake_case for most fields
 * (see `toTriggerDeliveryWire`). Reads unwrap the
 * `{ success, message, data }` envelope (paginated reads also return the sibling
 * `pagination`). All calls are tenant-scoped by the JWT `subsidiaryId` claim
 * injected by the client interceptors.
 */
import { integrationsClient } from '~/api/client';
import { endpoints } from '~/api/endpoints';
import type { ApiResponse } from '~/features/onboarding/types/connection.types';
import { extractDispositionFilename, triggerBrowserDownload } from '~/utils/download-file';

import type {
  BlockedCausesRequest,
  BlockedCausesResult,
  BlockedExportQuery,
  BlockedProductsRequest,
  BlockedProductsResult,
  BatchJobStatus,
  DeliveryControlResponse,
  DeliveryPlan,
  DeliveryPlanRequest,
  LatestDeliveryBySource,
  PlanProductDetail,
  PlanProductDetailRequest,
  PlanProductsPage,
  PlanProductsRequest,
  StartBatchJobRequest,
  StartBatchJobResponse,
  DeliveryJobResponse,
  DeliveryLineageNode,
  DeliveryPagination,
  DeliveryReconciliation,
  DeliveryReport,
  DeliveryRunDetail,
  DeliveryRunSummary,
  JobReconciliation,
  JobReconciliationQuery,
  ProductLeafPage,
  ProductLookupIdType,
  ProductLookupRequest,
  ProductLookupResult,
  ProductLookupRow,
  ProductOutcomeOption,
  ProductsUnavailableReason,
  RetryFailedResponse,
  RunListFilters,
  RunListPage,
  RunProductsQuery,
  RunTree,
  RunTreeQuery,
  TimelineStep,
  TriggerDeliveryRequest,
  WorkUnitNode,
  WorkUnitPage,
  DeliverableFields,
} from '../types/delivery-runs.types';

/** Paginated Delivery API envelope: `data[]` alongside a `pagination` block. */
interface PaginatedEnvelope<T> {
  success: boolean;
  message: string;
  data: T[];
  pagination: DeliveryPagination;
}

/** Primitive query param values Ky accepts on `searchParams`. */
type ParamValue = string | number | boolean;

/** Retry policy for the blob GET (CSV export) — matches the app's other file-download services. */
const DOWNLOAD_RETRY = { limit: 2, methods: ['get'], statusCodes: [408, 429, 500, 502, 503, 504] };

/** Build a Ky `searchParams` record, dropping null/undefined entries. */
function toSearchParams(params: Record<string, ParamValue | null | undefined>) {
  const out: Record<string, ParamValue> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== null && value !== undefined) out[key] = value;
  }
  return out;
}

// ---------------------------------------------------------------------------
// Run list + detail
// ---------------------------------------------------------------------------

/** Paginated list of delivery-classified runs for the active tenant. */
export async function listDeliveryRuns(filters: RunListFilters = {}): Promise<RunListPage> {
  const res = await integrationsClient
    .get(endpoints.deliveries.runs(), {
      searchParams: toSearchParams({
        family: filters.family,
        status: filters.status,
        connectionId: filters.connectionId,
        isDryRun: filters.isDryRun,
        parentRunId: filters.parentRunId,
        sortBy: filters.sortBy,
        page: filters.page,
        size: filters.size,
      }),
    })
    .json<PaginatedEnvelope<DeliveryRunSummary>>();
  return { items: res.data, pagination: res.pagination };
}

/** Full detail for one run (summary fields + timing/signals/currentStage/lineage). */
export async function getDeliveryRun(runId: string): Promise<DeliveryRunDetail> {
  const res = await integrationsClient
    .get(endpoints.deliveries.run(runId))
    .json<ApiResponse<DeliveryRunDetail>>();
  return res.data;
}

/** Run → work-unit tree; pass `includeProducts` to attach product leaves. */
export async function getDeliveryRunTree(
  runId: string,
  query: RunTreeQuery = {},
): Promise<RunTree> {
  const res = await integrationsClient
    .get(endpoints.deliveries.runTree(runId), {
      searchParams: toSearchParams({
        includeProducts: query.includeProducts,
        productStatus: query.productStatus,
        depth: query.depth,
      }),
    })
    .json<ApiResponse<RunTree>>();
  return res.data;
}

/** One page of a run's work units (no leaves). */
export async function listDeliveryRunUnits(
  runId: string,
  page = 1,
  size = 20,
): Promise<WorkUnitPage> {
  const res = await integrationsClient
    .get(endpoints.deliveries.runUnits(runId), { searchParams: { page, size } })
    .json<PaginatedEnvelope<WorkUnitNode>>();
  return { items: res.data, pagination: res.pagination };
}

// ---------------------------------------------------------------------------
// Product leaves (cursor pages)
// ---------------------------------------------------------------------------

/** Comma-join a multi-value filter, dropping blanks; undefined when empty so the param is omitted. */
function csv(values: string[] | undefined): string | undefined {
  const cleaned = (values ?? []).map((v) => v.trim()).filter(Boolean);
  return cleaned.length > 0 ? cleaned.join(',') : undefined;
}

/** Build the shared product-leaf query params (run-wide + per-unit reads). */
function productLeafParams(query: RunProductsQuery) {
  return toSearchParams({
    outcome: query.outcome,
    outcomes: csv(query.outcomes),
    excludeOutcomes: csv(query.excludeOutcomes),
    sku: query.sku,
    errorCode: query.errorCode,
    search: query.search,
    sortBy: query.sortBy,
    limit: query.limit,
    offset: query.offset,
  });
}

const PRODUCTS_UNAVAILABLE_REASONS: readonly ProductsUnavailableReason[] = [
  'NOT_TRACKED',
  'RETENTION_EXPIRED',
  'FAILED_BEFORE_PRODUCTS',
  'ALL_SUPERSEDED',
];

/** The value as a known reason, or `null` (absent, or a value this build doesn't know). */
function knownReason(value: unknown): ProductsUnavailableReason | null {
  return PRODUCTS_UNAVAILABLE_REASONS.find((r) => r === value) ?? null;
}

/**
 * Normalises a leaf page's "why no rows": the structured `productsUnavailable`
 * block wins over the flat `productsUnavailableReason` string (older servers send
 * only the string). An unknown reason becomes `null` on both, so callers fall back
 * to the plain empty state; a known one is mirrored into both fields.
 */
function toLeafPage(page: ProductLeafPage): ProductLeafPage {
  const block = page.productsUnavailable;
  const reason = knownReason(block?.reason) ?? knownReason(page.productsUnavailableReason);
  return {
    ...page,
    productsUnavailableReason: reason,
    productsUnavailable: reason ? { ...block, reason } : null,
  };
}

/** Cursor page of product leaves for one work unit. */
export async function getDeliveryUnitProducts(
  runId: string,
  unitIndex: number,
  query: RunProductsQuery = {},
): Promise<ProductLeafPage> {
  const res = await integrationsClient
    .get(endpoints.deliveries.runUnitProducts(runId, unitIndex), {
      searchParams: productLeafParams(query),
    })
    .json<ApiResponse<ProductLeafPage>>();
  return toLeafPage(res.data);
}

/** Cursor page of product leaves across every unit in the run. */
export async function getDeliveryRunProducts(
  runId: string,
  query: RunProductsQuery = {},
): Promise<ProductLeafPage> {
  const res = await integrationsClient
    .get(endpoints.deliveries.runProducts(runId), { searchParams: productLeafParams(query) })
    .json<ApiResponse<ProductLeafPage>>();
  return toLeafPage(res.data);
}

/**
 * The outcome filter menu. Static (no run id): each option carries the label
 * to render and the query to forward; exactly one is the default with an
 * empty query. Tolerates a bare array or a `{ options }` wrapper.
 */
export async function getProductOutcomes(): Promise<ProductOutcomeOption[]> {
  const res = await integrationsClient
    .get(endpoints.deliveries.productOutcomes())
    .json<ApiResponse<{ options?: ProductOutcomeOption[] } | ProductOutcomeOption[]>>();
  const data = res.data;
  const list = Array.isArray(data) ? data : (data.options ?? []);
  return list.filter((o) => typeof o.id === 'string' && typeof o.label === 'string');
}

/**
 * Batch by-id read on a connection: each product's latest audit row plus the
 * ids that matched nothing. Ids are trimmed, de-duplicated and capped at the
 * endpoint's 1000 before sending.
 */
export async function lookupProducts(
  connectionId: string,
  request: ProductLookupRequest,
): Promise<ProductLookupResult> {
  const ids = Array.from(new Set(request.ids.map((id) => id.trim()).filter(Boolean))).slice(
    0,
    1000,
  );
  const body = {
    ids,
    idType: request.idType ?? 'auto',
    ...(request.deliveryJobId ? { deliveryJobId: request.deliveryJobId } : {}),
    ...(request.includePayload ? { includePayload: true } : {}),
  };
  const res = await integrationsClient
    .post(endpoints.deliveries.productsLookup(connectionId), { json: body })
    .json<
      ApiResponse<{
        products?: ProductLookupRow[];
        items?: ProductLookupRow[];
        notFound?: string[];
        requested?: number;
        found?: number;
        truncated?: boolean;
        idType?: ProductLookupIdType;
        deliveryJobId?: string | null;
      }>
    >();
  const data = res.data;
  const products = data.products ?? data.items ?? [];
  // Counts fall back to what we can see so a pre-#627 response still renders;
  // `truncated` defaults to false because a missing flag is never a silent cap.
  return {
    products,
    notFound: Array.isArray(data.notFound) ? data.notFound : [],
    requested: typeof data.requested === 'number' ? data.requested : ids.length,
    found: typeof data.found === 'number' ? data.found : products.length,
    truncated: data.truncated === true,
    idType: data.idType ?? body.idType,
    deliveryJobId: data.deliveryJobId ?? request.deliveryJobId ?? null,
  };
}

// ---------------------------------------------------------------------------
// Blocked-product worklist + job reconciliation (BE PR #628)
// ---------------------------------------------------------------------------

/** Contract's documented range for every `limit` param on this surface. */
const BLOCKED_LIMIT_RANGE = { min: 1, max: 200 } as const;

/** Clamp a limit into the contract's 1–200 range instead of letting the server 422 it. */
function clampBlockedLimit(limit: number | undefined, fallback: number): number {
  if (limit == null || !Number.isFinite(limit)) return fallback;
  return Math.min(BLOCKED_LIMIT_RANGE.max, Math.max(BLOCKED_LIMIT_RANGE.min, Math.trunc(limit)));
}

/** Shared filter body for the blocked-products family; `since`/`deliveryJobId` omitted when unset. */
function blockedFiltersBody(filters: BlockedProductsRequest['filters']) {
  return {
    ...(filters?.deliveryJobId ? { deliveryJobId: filters.deliveryJobId } : {}),
    ...(filters?.since ? { since: filters.since } : {}),
    includeMock: filters?.includeMock ?? false,
  };
}

/**
 * Cause-grouped worklist of a connection's blocked products — "what is broken,
 * and how many products per cause?". `limit` is the number of CAUSES returned,
 * not products; `totalBlockedProducts` is the true headline count regardless
 * of how many causes fit under `limit`.
 */
export async function getBlockedCauses(
  connectionId: string,
  request: BlockedCausesRequest = {},
): Promise<BlockedCausesResult> {
  const body = {
    limit: clampBlockedLimit(request.limit, 50),
    filters: blockedFiltersBody(request.filters),
  };
  const res = await integrationsClient
    .post(endpoints.deliveries.blockedCauses(connectionId), { json: body })
    .json<ApiResponse<Partial<BlockedCausesResult>>>();
  const data = res.data;
  return {
    causes: data.causes ?? [],
    totalBlockedProducts: data.totalBlockedProducts ?? 0,
    truncated: data.truncated === true,
  };
}

/** Drill into one cause's blocked products, paged. */
export async function getBlockedProducts(
  connectionId: string,
  request: BlockedProductsRequest,
): Promise<BlockedProductsResult> {
  const body = {
    cause: request.cause,
    filters: blockedFiltersBody(request.filters),
    pagination: {
      page: request.page ?? 1,
      size: clampBlockedLimit(request.size, 50),
    },
  };
  const res = await integrationsClient
    .post(endpoints.deliveries.blockedProducts(connectionId), { json: body })
    .json<
      ApiResponse<{
        products?: BlockedProductsResult['products'];
        pagination?: DeliveryPagination;
        cause?: string;
      }>
    >();
  const data = res.data;
  return {
    products: data.products ?? [],
    pagination: data.pagination ?? null,
    cause: data.cause ?? request.cause,
  };
}

/**
 * Streams the blocked-products CSV and saves it as a file. Omit `cause` to
 * export every blocked product on the connection. The response carries a
 * `Content-Disposition` filename; a scope-aware name is generated as a
 * fallback (formula-injection characters in cell values are already
 * apostrophe-escaped server-side — nothing to do on this side).
 */
export async function downloadBlockedProductsCsv(
  connectionId: string,
  query: BlockedExportQuery = {},
): Promise<void> {
  const response = await integrationsClient.get(endpoints.deliveries.blockedExport(connectionId), {
    searchParams: toSearchParams({
      cause: query.cause,
      deliveryJobId: query.deliveryJobId,
      since: query.since,
      includeMock: query.includeMock,
    }),
    retry: DOWNLOAD_RETRY,
  });
  const blob = await response.blob();
  const scope = query.cause ? `-${query.cause}` : '-all';
  const filename =
    extractDispositionFilename(response.headers.get('content-disposition')) ??
    `blocked-products-${connectionId}${scope}.csv`;
  triggerBrowserDownload(blob, filename);
}

/**
 * Did this job's failures get fixed in a later run? Headline tallies describe
 * the whole job regardless of `resolution`, which narrows only the returned
 * `products` list — never read the filtered list length as the true count.
 */
export async function getJobReconciliation(
  connectionId: string,
  jobId: string,
  query: JobReconciliationQuery = {},
): Promise<JobReconciliation> {
  const res = await integrationsClient
    .get(endpoints.deliveries.jobReconciliation(connectionId, jobId), {
      searchParams: toSearchParams({
        resolution: query.resolution,
        includeMock: query.includeMock,
        limit: query.limit != null ? clampBlockedLimit(query.limit, 100) : undefined,
        offset: query.offset,
      }),
    })
    .json<ApiResponse<JobReconciliation>>();
  return res.data;
}

// ---------------------------------------------------------------------------
// Timeline · lineage · report · reconcile
// ---------------------------------------------------------------------------

/** Chronological stage-trace timeline for a run. */
export async function getDeliveryRunTimeline(runId: string): Promise<TimelineStep[]> {
  const res = await integrationsClient
    .get(endpoints.deliveries.runTimeline(runId))
    .json<ApiResponse<TimelineStep[]>>();
  return res.data;
}

/** Retry-chain lineage (`[root, *root's retries]`) with recovery projection. */
export async function getDeliveryRunLineage(runId: string): Promise<DeliveryLineageNode[]> {
  const res = await integrationsClient
    .get(endpoints.deliveries.runLineage(runId))
    .json<ApiResponse<DeliveryLineageNode[]>>();
  return res.data;
}

/** Run report — totals, timing (+percentiles), failures, per-unit rollups. */
export async function getDeliveryRunReport(runId: string): Promise<DeliveryReport> {
  const res = await integrationsClient
    .get(endpoints.deliveries.runReport(runId))
    .json<ApiResponse<DeliveryReport>>();
  return res.data;
}

/** Outcome-bucket reconciliation; `includeClickhouse` adds the optional CH tally. */
export async function getDeliveryRunReconciliation(
  runId: string,
  includeClickhouse = false,
): Promise<DeliveryReconciliation> {
  const res = await integrationsClient
    .get(endpoints.deliveries.runReconcile(runId), {
      searchParams: toSearchParams({ includeClickhouse }),
    })
    .json<ApiResponse<DeliveryReconciliation>>();
  return res.data;
}

// ---------------------------------------------------------------------------
// Control actions (act on the live Temporal workflow)
// ---------------------------------------------------------------------------

/** Pause a run at the next page boundary. */
export async function pauseDeliveryRun(runId: string): Promise<DeliveryControlResponse> {
  const res = await integrationsClient
    .post(endpoints.deliveries.runPause(runId))
    .json<ApiResponse<DeliveryControlResponse>>();
  return res.data;
}

/** Resume a paused run. */
export async function resumeDeliveryRun(runId: string): Promise<DeliveryControlResponse> {
  const res = await integrationsClient
    .post(endpoints.deliveries.runResume(runId))
    .json<ApiResponse<DeliveryControlResponse>>();
  return res.data;
}

/** Cancel a run (durable Temporal cancel + Postgres CANCELLED). */
export async function cancelDeliveryRun(runId: string): Promise<DeliveryControlResponse> {
  const res = await integrationsClient
    .post(endpoints.deliveries.runCancel(runId))
    .json<ApiResponse<DeliveryControlResponse>>();
  return res.data;
}

/** Retry only the failed products into a new run linked to this one. */
export async function retryFailedDeliveryRun(runId: string): Promise<RetryFailedResponse> {
  const res = await integrationsClient
    .post(endpoints.deliveries.runRetryFailed(runId))
    .json<ApiResponse<RetryFailedResponse>>();
  return res.data;
}

// ---------------------------------------------------------------------------
// Trigger (deliver / dry-run)
// ---------------------------------------------------------------------------

/**
 * Map the camelCase trigger body onto the wire shape. The backend's
 * `TriggerDeliveryRequest` only accepts snake_case for these fields and silently
 * drops unknown keys, so sending camelCase ran every trigger as `API` with no
 * `workflow_id`. `uploadId` / `appliedFilters` / `callbackUrl` declare camelCase
 * aliases and stay as-is; `appliedFilters` is passed through unconverted.
 */
export function toTriggerDeliveryWire(body: TriggerDeliveryRequest): Record<string, unknown> {
  const wire: Record<string, unknown> = {
    product_ids: body.productIds,
    force_redeliver: body.forceRedeliver,
    delivery_method: body.deliveryMethod,
    feed_format: body.feedFormat,
    feedonomics_feed_profile: body.feedonomicsFeedProfile,
    batch_size: body.batchSize,
    row_limit: body.rowLimit,
    field_filter: body.fieldFilter,
    workflow_id: body.workflowId,
    write_analytics: body.writeAnalytics,
    write_payloads: body.writePayloads,
    use_export_config: body.useExportConfig,
    export_template: body.exportTemplate,
    export_locale: body.exportLocale,
    transformer_id: body.transformerId,
    scope: body.scope,
    uploadId: body.uploadId,
    appliedFilters: body.appliedFilters,
    callbackUrl: body.callbackUrl,
    // Attribute selection for a redelivery — the same fields first-time delivery
    // sends on the internal trigger; these declare camelCase aliases.
    deliveryFields: body.deliveryFields,
    searchDescriptors: body.searchDescriptors,
    targetLocales: body.targetLocales,
    enrichmentJobId: body.enrichmentJobId,
  };
  // Drop unset keys so backend defaults apply and the payload stays readable in devtools.
  return Object.fromEntries(Object.entries(wire).filter(([, value]) => value !== undefined));
}

/** Trigger a delivery for a connection. */
export async function triggerDelivery(
  connectionId: string,
  body: TriggerDeliveryRequest,
): Promise<DeliveryJobResponse> {
  const res = await integrationsClient
    .post(endpoints.deliveries.deliver(connectionId), { json: toTriggerDeliveryWire(body) })
    .json<ApiResponse<DeliveryJobResponse>>();
  return res.data;
}

/** Trigger a dry-run (end-to-end minus the destination push). */
export async function triggerDeliveryDryRun(
  connectionId: string,
  body: TriggerDeliveryRequest,
): Promise<DeliveryJobResponse> {
  const res = await integrationsClient
    .post(endpoints.deliveries.dryRun(connectionId), { json: toTriggerDeliveryWire(body) })
    .json<ApiResponse<DeliveryJobResponse>>();
  return res.data;
}

// ---------------------------------------------------------------------------
// Delivery wizard: plan, latest-by-source, batch jobs
// ---------------------------------------------------------------------------

/** Read-only breakdown of what a trigger with this scope would read. */
export async function planDelivery(
  connectionId: string,
  body: DeliveryPlanRequest,
): Promise<DeliveryPlan> {
  const res = await integrationsClient
    .post(endpoints.deliveries.plan(connectionId), { json: body })
    .json<ApiResponse<DeliveryPlan>>();
  return res.data;
}

/** Newest run per source batch on a connection; ids without a run are omitted by the server. */
export async function getLatestDeliveryBySource(
  connectionId: string,
  sourceWorkflowIds: readonly string[],
): Promise<LatestDeliveryBySource[]> {
  const searchParams = new URLSearchParams({ connectionId });
  for (const id of sourceWorkflowIds) searchParams.append('sourceWorkflowIds', id);
  const res = await integrationsClient
    .get(endpoints.deliveries.latestBySource(), { searchParams })
    .json<ApiResponse<LatestDeliveryBySource[]>>();
  return res.data;
}

/** Start (or join, the id is deterministic) a delivery job over several batches. */
export async function startBatchJob(
  connectionId: string,
  body: StartBatchJobRequest,
): Promise<StartBatchJobResponse> {
  const res = await integrationsClient
    .post(endpoints.deliveries.batchJobs(connectionId), { json: body })
    .json<ApiResponse<StartBatchJobResponse>>();
  return res.data;
}

/** Current state of a batch job and each batch's run. */
export async function getBatchJob(connectionId: string, jobId: string): Promise<BatchJobStatus> {
  const res = await integrationsClient
    .get(endpoints.deliveries.batchJob(connectionId, jobId))
    .json<ApiResponse<BatchJobStatus>>();
  return res.data;
}

/** Stop a batch job after the run in flight (202 with the job's current status). */
export async function cancelBatchJob(connectionId: string, jobId: string): Promise<BatchJobStatus> {
  const res = await integrationsClient
    .post(endpoints.deliveries.batchJobCancel(connectionId, jobId))
    .json<ApiResponse<BatchJobStatus>>();
  return res.data;
}

/** One page of the Breakdown products grid (keyset on SKU). */
export async function planDeliveryProducts(
  connectionId: string,
  body: PlanProductsRequest,
): Promise<PlanProductsPage> {
  const res = await integrationsClient
    .post(endpoints.deliveries.planProducts(connectionId), { json: body })
    .json<ApiResponse<PlanProductsPage>>();
  return res.data;
}

/** The fields a delivery to this connection can send (the field picker's options). */
export async function getDeliverableFields(connectionId: string): Promise<DeliverableFields> {
  const res = await integrationsClient
    .get(endpoints.deliveries.deliverableFields(connectionId))
    .json<ApiResponse<DeliverableFields>>();
  return res.data;
}

/** One product's "what will be sent" detail, compared with its last delivery. */
export async function planProductDetail(
  connectionId: string,
  body: PlanProductDetailRequest,
): Promise<PlanProductDetail> {
  const res = await integrationsClient
    .post(endpoints.deliveries.planProductDetail(connectionId), { json: body })
    .json<ApiResponse<PlanProductDetail>>();
  return res.data;
}
